import { FormStore, GlobalStore } from "@roc/feature-app-core";
import { action, flow, makeObservable, observable } from "mobx";
import { isSecureLinkError } from "@roc-capital/secure-link";
import { GENERIC_ERROR_MESSAGE } from "@roc/feature-utils";
import { SecureLinkReferralFeeService, SecureLinkRequest } from "../services/secureLinkReferralFeeService";

const noSession: SecureLinkRequest = () =>
  Promise.reject(new Error('ReferralAuthStore: call setRequest() with the session request first'));

const SIGNED_STATUSES = ['Pending Approval', 'Approved'];

const form = {
  fields: {
    bankName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'bank name'
    },
    accountName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account name',
    },
    accountNumber: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account number',
    },
    routingNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{9}$/',
      message: 'The routing number must be 9 digits.'
    },
    streetName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Street Name field is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The City field is required.',
    },
    zipCode: {
      value: '',
      error: null,
      message: 'Zip Code must be 5 digits',
      rule: 'required|regex:/^\\d{5}$/|alpha_dash',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The State field is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};


/**
 * The referral fee page (/link/referral-fee). Every call goes to lender-service's session-guarded endpoints; the loan
 * comes from the secure link session on the server.
 *
 * Created with the app's root store, before there is a session: the page passes the session's request in with
 * setRequest() once useSecureLinkSession has one. Until then every call rejects.
 */
export class ReferralAuthStore extends FormStore {
  private secureLinkService: SecureLinkReferralFeeService;
  public globalStore: GlobalStore;
  public activeStep: number;
  public entityName: string;
  public loading: boolean;
  public isSigned: boolean;
  public requestComplete: boolean;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.setRequest(noSession);
    this.activeStep = 0;
    this.entityName = '';
    this.loading = false;
    this.isSigned = false;
    this.requestComplete = false;
    makeObservable(this, {
      activeStep: observable,
      entityName: observable,
      loading: observable,
      isSigned: observable,
      requestComplete: observable,
      moveToSignatureStep: action,
      goPrevStep: action,
      loadData: flow,
      submit: flow,
    });
  }

  /** The authorized fetch from useSecureLinkSession; every call goes through it. */
  setRequest(request: SecureLinkRequest) {
    this.secureLinkService = new SecureLinkReferralFeeService(request);
  }

  moveToSignatureStep() {
    this.runFormValidationWithMessage();
    if (this.form.meta.isValid) {
      this.activeStep = 1;
    }
  }

  goPrevStep() {
    this.activeStep = 0;
  }

  /** The broker's name and whether its bank details were already sent. */
  *loadData() {
    this.loading = true;
    try {
      const data = yield this.secureLinkService.getData();
      this.entityName = data?.entityName ?? '';
      this.isSigned = SIGNED_STATUSES.includes(data?.bankInfoStatus);
      this.activeStep = this.isSigned ? 1 : 0;
    } catch (error) {
      if (isSecureLinkError(error) && error.kind === 'COMPLETED') {
        // Already sent through this link: the link is done.
        this.isSigned = true;
      } else if (!(isSecureLinkError(error) && error.kind === 'INVALID')) {
        // INVALID: the page switches to "session ended"; no toast needed.
        this.globalStore.notificationStore.showErrorNotification({ message: GENERIC_ERROR_MESSAGE });
      }
    } finally {
      this.loading = false;
      this.requestComplete = true;
    }
  }

  *submit(signature: string) {
    this.loading = true;
    try {
      const values = this.getFormValues();
      yield this.secureLinkService.sign({
        accountName: values.accountName,
        routingNumber: values.routingNumber,
        accountNumber: values.accountNumber,
        bankName: values.bankName,
        streetName: values.streetName,
        city: values.city,
        zipCode: values.zipCode,
        state: values.state,
        signature,
        userAgent: navigator.userAgent,
      });
      this.isSigned = true;
    } catch (error) {
      if (isSecureLinkError(error) && error.kind === 'COMPLETED') {
        // Sent already (e.g. in another tab): the link is done.
        this.isSigned = true;
      } else if (!(isSecureLinkError(error) && error.kind === 'INVALID')) {
        this.globalStore.notificationStore.showErrorNotification({
          message: (isSecureLinkError(error) && error.message) || GENERIC_ERROR_MESSAGE,
        });
      }
    } finally {
      this.loading = false;
    }
  }
}
